/**
 * Haseeb brand colours as literal hex, for the places CSS custom properties
 * cannot reach: Recharts props, inline SVG attributes, canvas drawing.
 *
 * Anything that CAN take a class should use the Tailwind tokens instead
 * (bg-surface, text-ink-secondary, from-takbirah-deep, …) — those already flip
 * with the mode. Use `useBrand()` here and the values re-read on every mode flip.
 *
 * Source of truth: the Haseeb Brand Colour Guideline, "Brass Bridge on Pure Grey".
 * Every tier is a two-stop gradient, deep -> bright. Dark mode has its own
 * gradients; it is never a dimmed version of light.
 */

import type { ThemeMode } from './theme';

export interface TierStops {
  readonly deep: string;
  readonly bright: string;
}

export interface BrandPalette {
  /* Ground — Pure Grey, untinted */
  readonly canvas: string;
  readonly surface: string;
  readonly fill: string;
  readonly separator: string;
  /* Text ramp */
  readonly text1: string;
  readonly text2: string;
  readonly text3: string;
  /* Always legible on a gradient fill */
  readonly onFill: string;
  /* Unfilled arc / progress track */
  readonly track: string;
  /* The four tiers */
  readonly takbirah: TierStops;
  readonly jamaa: TierStops;
  readonly onTime: TierStops;
  readonly missed: TierStops;
}

const LIGHT: BrandPalette = {
  canvas: '#E6E6E6',
  surface: '#F2F2F2',
  fill: '#DCDCDC',
  separator: '#CECECE',
  text1: '#1A1D21',
  text2: '#5A6070',
  text3: '#8A909E',
  onFill: '#F2F4F7',
  track: '#D4D6DB',
  takbirah: { deep: '#101C4A', bright: '#33488A' },
  jamaa: { deep: '#235882', bright: '#4E8CB4' },
  onTime: { deep: '#7E5E14', bright: '#B8942E' },
  missed: { deep: '#7E243A', bright: '#B4526A' },
};

const DARK: BrandPalette = {
  canvas: '#141414',
  surface: '#1E1E1E',
  fill: '#292929',
  separator: '#333333',
  text1: '#F2F4F7',
  text2: '#A6ADBB',
  text3: '#7C8492',
  onFill: '#F2F4F7',
  track: '#292929',
  takbirah: { deep: '#384C8D', bright: '#5A72B4' },
  jamaa: { deep: '#5494BF', bright: '#79B4DA' },
  onTime: { deep: '#C6A34D', bright: '#E4C576' },
  missed: { deep: '#C35C72', bright: '#E18497' },
};

export const BRAND: Record<ThemeMode, BrandPalette> = {
  light: LIGHT,
  dark: DARK,
};

export function paletteFor(mode: ThemeMode): BrandPalette {
  return BRAND[mode];
}

/** The four tiers in ladder order: best first, worst last. Never reorder these. */
export function tierLadder(palette: BrandPalette): readonly TierStops[] {
  return [palette.takbirah, palette.jamaa, palette.onTime, palette.missed];
}

export type TierName = 'takbirah' | 'jamaa' | 'onTime' | 'missed';

/**
 * Every stop in the system, so brand colours are recognised before any guessing.
 * The stop is kept too, which is what gives habits eight distinct identities
 * instead of four.
 */
const STOP_BY_HEX: Readonly<Record<string, { tier: TierName; stop: keyof TierStops }>> = {
  '#101c4a': { tier: 'takbirah', stop: 'deep' }, '#33488a': { tier: 'takbirah', stop: 'bright' },
  '#384c8d': { tier: 'takbirah', stop: 'deep' }, '#5a72b4': { tier: 'takbirah', stop: 'bright' },
  '#235882': { tier: 'jamaa', stop: 'deep' }, '#4e8cb4': { tier: 'jamaa', stop: 'bright' },
  '#5494bf': { tier: 'jamaa', stop: 'deep' }, '#79b4da': { tier: 'jamaa', stop: 'bright' },
  '#7e5e14': { tier: 'onTime', stop: 'deep' }, '#b8942e': { tier: 'onTime', stop: 'bright' },
  '#c6a34d': { tier: 'onTime', stop: 'deep' }, '#e4c576': { tier: 'onTime', stop: 'bright' },
  '#7e243a': { tier: 'missed', stop: 'deep' }, '#b4526a': { tier: 'missed', stop: 'bright' },
  '#c35c72': { tier: 'missed', stop: 'deep' }, '#e18497': { tier: 'missed', stop: 'bright' },
};

/**
 * Hue plus chroma (max channel minus min, 0-1).
 *
 * Chroma rather than HSL saturation on purpose: saturation is divided by
 * lightness, so a pale tint like #E2E8F0 scores a misleadingly high 0.32 and
 * would be read as a real blue. Chroma calls it what it is — a near-grey.
 */
function hueAndChroma(hex: string): { hue: number; chroma: number } | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return null;

  const value = parseInt(match[1]!, 16);
  const r = ((value >> 16) & 255) / 255;
  const g = ((value >> 8) & 255) / 255;
  const b = (value & 255) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const chroma = max - min;
  if (chroma === 0) return { hue: 0, chroma: 0 };

  let hue: number;
  if (max === r) hue = ((g - b) / chroma) % 6;
  else if (max === g) hue = (b - r) / chroma + 2;
  else hue = (r - g) / chroma + 4;

  return { hue: (hue * 60 + 360) % 360, chroma };
}

/** Which tier an arbitrary colour belongs to, by hue family. */
export function tierForColor(hex: string): TierName {
  const known = STOP_BY_HEX[hex.trim().toLowerCase()];
  if (known) return known.tier;

  const hsl = hueAndChroma(hex);
  // Unparseable or near-grey: fall back to the brand anchor.
  if (!hsl || hsl.chroma < 0.1) return 'takbirah';

  const { hue } = hsl;
  if (hue >= 15 && hue < 70) return 'onTime';     // amber, brass, orange
  if (hue >= 70 && hue < 170) return 'takbirah';  // greens
  if (hue >= 170 && hue < 255) return 'jamaa';    // cyan through blue
  if (hue >= 255 && hue < 320) return 'takbirah'; // indigo, violet
  return 'missed';                                // magenta, pink, rose, red
}

/**
 * Habit colours live in saved user data, so old accounts still carry hues the
 * brand retired (emerald, sky, violet…). Rather than rewrite anyone's data, map
 * whatever is stored onto the nearest tier and render the current mode's stop.
 * The result is always one of the eight legal stops.
 */
export function resolveHabitColor(
  raw: string | null | undefined,
  palette: BrandPalette,
  fallbackStop: keyof TierStops = 'bright',
): string {
  if (!raw) return palette.takbirah[fallbackStop];

  const known = STOP_BY_HEX[raw.trim().toLowerCase()];
  if (known) return palette[known.tier][known.stop];

  return palette[tierForColor(raw)][fallbackStop];
}
